import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  ChargeType,
  FulfillmentType,
  PaymentMethod,
  ResourceType,
  ServiceRequestType,
} from '@/lib/shared-types';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import {
  MAX_DIMENSIONS,
  MAX_MEASURES,
  sharedDimensions,
  type AnalyticsMeta,
  type BranchOption,
  type MeasureMeta,
} from '../analytics';
import {
  COMPARISON_MODES,
  DATE_PRESETS,
  REPORT_LAYOUTS,
  REPORT_MAX_FILTER_VALUES,
  type ReportDefinitionInput,
} from '../reporting-types';

/**
 * Dimensions with a small, stable enum of values — sourced from the same
 * package `badge.tsx` reads its status table from, not invented here. Every
 * other dimension's values are catalogue rows (items, waiters, tables…) with
 * no lookup endpoint in this contract, so their filter is a raw id typed in;
 * see the report for why that is a real gap rather than an oversight.
 */
const ENUM_FILTER_VALUES: Record<string, Record<string, string>> = {
  fulfillmentType: FulfillmentType,
  paymentMethod: PaymentMethod,
  chargeType: ChargeType,
  serviceRequestType: ServiceRequestType,
  resourceType: ResourceType,
};

/** Each of the enums above is already translated elsewhere in the app; reused
 *  rather than re-keyed under a namespace of this screen's own. */
const ENUM_FILTER_I18N_NAMESPACE: Record<string, string> = {
  fulfillmentType: 'order.fulfillment',
  paymentMethod: 'billing.method',
  chargeType: 'billing.chargeType',
  serviceRequestType: 'platform.serviceQueue.typeLabel',
  resourceType: 'spaces.resourceType',
};

/** Filtering on a calendar dimension is a different UI (the date preset above
 *  it already covers `date`); temporal dimensions are left out of the picker. */
function isFilterable(dimension: AnalyticsMeta['dimensions'][number]): boolean {
  return !dimension.temporal;
}

function Chip({
  on,
  disabled,
  title,
  onClick,
  children,
}: {
  on: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={
        on
          ? 'rounded-full bg-accent-solid px-3 py-1.5 text-sm text-accent-on'
          : 'rounded-full border px-3 py-1.5 text-sm disabled:opacity-40'
      }
    >
      {children}
    </button>
  );
}

export function DefinitionEditor({
  meta,
  value,
  onChange,
  branches,
}: {
  meta: AnalyticsMeta;
  value: ReportDefinitionInput;
  onChange: (next: ReportDefinitionInput) => void;
  branches: BranchOption[];
}) {
  const { t } = useTranslation();

  const measuresByName = useMemo(() => new Map(meta.measures.map((m) => [m.name, m])), [meta]);

  const chosenMeasures = useMemo(
    () => value.measures.map((name) => measuresByName.get(name)).filter((m): m is MeasureMeta => !!m),
    [value.measures, measuresByName],
  );
  // The dimensions every selected measure can reach — the row axis and the
  // pivot axis both draw from this, since both are group-bys over the same
  // measures.
  const reachable = useMemo(() => sharedDimensions(chosenMeasures), [chosenMeasures]);

  const totalGrouping = value.dimensions.length + value.pivotColumns.length;

  const toggleMeasure = (name: string) => {
    const on = value.measures.includes(name);
    if (!on && value.measures.length >= MAX_MEASURES) return;
    onChange({
      ...value,
      measures: on ? value.measures.filter((m) => m !== name) : [...value.measures, name],
    });
  };

  const toggleGrouping = (axis: 'dimensions' | 'pivotColumns', name: string) => {
    const on = value[axis].includes(name);
    if (!on && totalGrouping >= MAX_DIMENSIONS) return;
    const otherAxis = axis === 'dimensions' ? 'pivotColumns' : 'dimensions';
    onChange({
      ...value,
      [axis]: on ? value[axis].filter((d) => d !== name) : [...value[axis], name],
      // A dimension cannot sit on both axes — picking it here drops it there.
      [otherAxis]: value[otherAxis].filter((d) => d !== name),
    });
  };

  const setLayout = (layout: ReportDefinitionInput['layout']) => {
    onChange({
      ...value,
      layout,
      // Tabular carries no pivot columns; a pivot column left behind from a
      // previous layout would fail REPORT_PIVOT_COLUMNS_WITHOUT_PIVOT on preview.
      pivotColumns: layout === 'tabular' ? [] : value.pivotColumns,
      // A pivot cannot also carry a comparison (REPORT_PIVOT_COMPARISON_UNSUPPORTED).
      comparison: layout === 'pivot' ? 'none' : value.comparison,
    });
  };

  const filterableDimensions = meta.dimensions.filter(
    (d) => d.permitted && isFilterable(d) && !(d.name in value.filters),
  );

  const addFilter = (dimension: string) => {
    if (!dimension) return;
    onChange({ ...value, filters: { ...value.filters, [dimension]: [] } });
  };

  const removeFilter = (dimension: string) => {
    const next = { ...value.filters };
    delete next[dimension];
    onChange({ ...value, filters: next });
  };

  const setFilterValues = (dimension: string, values: string[]) => {
    onChange({
      ...value,
      filters: { ...value.filters, [dimension]: values.slice(0, REPORT_MAX_FILTER_VALUES) },
    });
  };

  const sortable = [...value.measures, ...value.dimensions, ...value.pivotColumns];

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------ measures */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('reports.builder.measures')}</legend>
        <p className="text-xs text-ink-muted">
          {t('reports.builder.measuresHint', { max: MAX_MEASURES })}
        </p>
        <div className="flex flex-wrap gap-2">
          {meta.measures.map((measure) => {
            const on = value.measures.includes(measure.name);
            return (
              <Chip
                key={measure.name}
                on={on}
                title={
                  measure.permitted
                    ? measure.description
                    : t('reports.builder.notEntitled', { permissions: measure.requires.join(', ') })
                }
                disabled={!measure.permitted || (!on && value.measures.length >= MAX_MEASURES)}
                onClick={() => toggleMeasure(measure.name)}
              >
                {t(`reports.measures.${measure.name}`, measure.name)}
              </Chip>
            );
          })}
        </div>
      </fieldset>

      {/* ------------------------------------------------------------- layout */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('reporting.builder.layout')}</legend>
        <div className="flex gap-2">
          {REPORT_LAYOUTS.map((layout) => (
            <Chip key={layout} on={value.layout === layout} onClick={() => setLayout(layout)}>
              {t(`reporting.layout.${layout}`)}
            </Chip>
          ))}
        </div>
      </fieldset>

      {/* --------------------------------------------------------- dimensions */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">
          {value.layout === 'pivot' ? t('reporting.builder.rows') : t('reports.builder.dimensions')}
        </legend>
        <p className="text-xs text-ink-muted">
          {t('reports.builder.dimensionsHint', { max: MAX_DIMENSIONS })}
        </p>
        <div className="flex flex-wrap gap-2">
          {meta.dimensions.map((dimension) => {
            const on = value.dimensions.includes(dimension.name);
            const canReach = reachable.includes(dimension.name);
            const blockedByOtherAxis = value.pivotColumns.includes(dimension.name);
            return (
              <Chip
                key={dimension.name}
                on={on}
                title={
                  !dimension.permitted
                    ? t('reports.builder.notEntitled', { permissions: dimension.requires.join(', ') })
                    : !canReach
                      ? t('reports.builder.unreachable')
                      : dimension.description
                }
                disabled={
                  !dimension.permitted ||
                  !canReach ||
                  blockedByOtherAxis ||
                  (!on && totalGrouping >= MAX_DIMENSIONS)
                }
                onClick={() => toggleGrouping('dimensions', dimension.name)}
              >
                {t(`reports.dimensions.${dimension.name}`, dimension.name)}
              </Chip>
            );
          })}
        </div>
      </fieldset>

      {value.layout === 'pivot' && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{t('reporting.builder.columns')}</legend>
          <p className="text-xs text-ink-muted">{t('reporting.builder.columnsHint')}</p>
          <div className="flex flex-wrap gap-2">
            {meta.dimensions.map((dimension) => {
              const on = value.pivotColumns.includes(dimension.name);
              const canReach = reachable.includes(dimension.name);
              const blockedByOtherAxis = value.dimensions.includes(dimension.name);
              return (
                <Chip
                  key={dimension.name}
                  on={on}
                  title={
                    !dimension.permitted
                      ? t('reports.builder.notEntitled', { permissions: dimension.requires.join(', ') })
                      : !canReach
                        ? t('reports.builder.unreachable')
                        : dimension.description
                  }
                  disabled={
                    !dimension.permitted ||
                    !canReach ||
                    blockedByOtherAxis ||
                    (!on && totalGrouping >= MAX_DIMENSIONS)
                  }
                  onClick={() => toggleGrouping('pivotColumns', dimension.name)}
                >
                  {t(`reports.dimensions.${dimension.name}`, dimension.name)}
                </Chip>
              );
            })}
          </div>
        </fieldset>
      )}

      {/* ------------------------------------------------------------ filters */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{t('reporting.builder.filters')}</legend>
        <div className="space-y-3">
          {Object.entries(value.filters).map(([dimension, values]) => (
            <FilterRow
              key={dimension}
              dimension={dimension}
              values={values ?? []}
              branches={branches}
              onChange={(next) => setFilterValues(dimension, next)}
              onRemove={() => removeFilter(dimension)}
            />
          ))}
        </div>
        {filterableDimensions.length > 0 && (
          <div className="w-56">
            <Select
              value=""
              onChange={(event) => addFilter(event.target.value)}
              aria-label={t('reporting.builder.addFilter')}
            >
              <option value="">{t('reporting.builder.addFilter')}</option>
              {filterableDimensions.map((dimension) => (
                <option key={dimension.name} value={dimension.name}>
                  {t(`reports.dimensions.${dimension.name}`, dimension.name)}
                </option>
              ))}
            </Select>
          </div>
        )}
      </fieldset>

      {/* --------------------------------------------------------------- date */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('reporting.builder.datePreset')}>
          <Select
            value={value.datePreset}
            onChange={(event) =>
              onChange({ ...value, datePreset: event.target.value as ReportDefinitionInput['datePreset'] })
            }
          >
            {DATE_PRESETS.map((preset) => (
              <option key={preset} value={preset}>
                {t(`reporting.datePreset.${preset}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('reporting.builder.comparison')}>
          <Select
            value={value.comparison}
            disabled={value.layout === 'pivot'}
            onChange={(event) =>
              onChange({ ...value, comparison: event.target.value as ReportDefinitionInput['comparison'] })
            }
          >
            {COMPARISON_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(`reporting.comparison.${mode}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {value.datePreset === 'custom' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('reports.filters.from')}>
            <Input
              type="date"
              value={value.from?.slice(0, 10) ?? ''}
              onChange={(event) =>
                onChange({
                  ...value,
                  from: event.target.value ? `${event.target.value}T00:00:00.000Z` : undefined,
                })
              }
            />
          </Field>
          <Field label={t('reports.filters.to')}>
            <Input
              type="date"
              value={value.to?.slice(0, 10) ?? ''}
              onChange={(event) =>
                onChange({
                  ...value,
                  to: event.target.value ? `${event.target.value}T23:59:59.999Z` : undefined,
                })
              }
            />
          </Field>
        </div>
      )}

      {/* ------------------------------------------------------- sort & limit */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t('reporting.builder.sortField')}>
          <Select
            value={value.sort?.field ?? ''}
            onChange={(event) =>
              onChange({
                ...value,
                sort: event.target.value
                  ? { field: event.target.value, direction: value.sort?.direction ?? 'desc' }
                  : undefined,
              })
            }
          >
            <option value="">{t('reporting.builder.sortNone')}</option>
            {sortable.map((name) => (
              <option key={name} value={name}>
                {t(`reports.measures.${name}`, t(`reports.dimensions.${name}`, name))}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('reporting.builder.sortDirection')}>
          <Select
            value={value.sort?.direction ?? 'desc'}
            disabled={!value.sort}
            onChange={(event) =>
              onChange({
                ...value,
                sort: value.sort
                  ? { ...value.sort, direction: event.target.value as 'asc' | 'desc' }
                  : undefined,
              })
            }
          >
            <option value="desc">{t('reporting.builder.sortDesc')}</option>
            <option value="asc">{t('reporting.builder.sortAsc')}</option>
          </Select>
        </Field>
        <Field label={t('reports.builder.limit')}>
          <Input
            type="number"
            min={1}
            max={meta.limits.maxRows}
            value={value.limit}
            onChange={(event) =>
              onChange({
                ...value,
                limit: Math.min(meta.limits.maxRows, Math.max(1, Number(event.target.value) || 1)),
              })
            }
          />
        </Field>
      </div>
    </div>
  );
}

function FilterRow({
  dimension,
  values,
  branches,
  onChange,
  onRemove,
}: {
  dimension: string;
  values: string[];
  branches: BranchOption[];
  onChange: (values: string[]) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const enumValues = ENUM_FILTER_VALUES[dimension];

  const toggle = (id: string) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-lg border p-3">
      <span className="mt-2 shrink-0 text-sm font-medium">
        {t(`reports.dimensions.${dimension}`, dimension)}
      </span>

      <div className="flex min-w-0 flex-1 flex-wrap gap-2">
        {dimension === 'branch' ? (
          branches.map((branch) => (
            <Chip key={branch.id} on={values.includes(branch.id)} onClick={() => toggle(branch.id)}>
              {branch.name}
            </Chip>
          ))
        ) : enumValues ? (
          Object.values(enumValues).map((option) => (
            <Chip key={option} on={values.includes(option)} onClick={() => toggle(option)}>
              {t(`${ENUM_FILTER_I18N_NAMESPACE[dimension]}.${option}`, option)}
            </Chip>
          ))
        ) : (
          <div className="w-full max-w-sm">
            <Input
              value={values.join(', ')}
              placeholder={t('reporting.builder.filterValuesPlaceholder')}
              onChange={(event) =>
                onChange(
                  event.target.value
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean),
                )
              }
            />
            <p className="mt-1 text-xs text-ink-muted">{t('reporting.builder.filterValuesHint')}</p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('common.remove')}
        className="ms-auto shrink-0 rounded p-1 text-ink-muted hover:bg-hover hover:text-ink"
      >
        <Icon as={X} size="sm" />
      </button>
    </div>
  );
}
